import {union} from 'lodash';
import {DateTime} from 'luxon';

import {getSortedWeekdays} from '../../../helpers/calendarHelpers';
import {CalendarsParameters, CalendarsViewTypesEnum} from './calendarsParameters';

interface ParametersRange {
  startsAt: number;
  endsAt: number;
}

export function findParametersRange(parameters: CalendarsParameters): ParametersRange {
  const {focusedDate, weekStartDay} = parameters;
  const focusedDateTime = DateTime.fromMillis(focusedDate);

  switch (parameters.viewType) {
    case CalendarsViewTypesEnum.PREVIEW:
      return {
        startsAt: focusedDateTime.startOf('day').valueOf(),
        endsAt: focusedDateTime.endOf('day').valueOf(),
      };

    case CalendarsViewTypesEnum.DAY:
      return {
        startsAt: focusedDateTime.startOf('day').minus({days: 3}).valueOf(),
        endsAt: focusedDateTime.endOf('day').plus({days: 3}).valueOf(),
      };

    case CalendarsViewTypesEnum.SCHEDULING:
    case CalendarsViewTypesEnum.WEEK: {
      // Figure out our position in the week to then infer the bounds.
      const weekdays = getSortedWeekdays(weekStartDay);
      const daysBefore = weekdays.indexOf(focusedDateTime.weekday);
      const daysAfter = 6 - daysBefore;

      // Artifically extend the range end by 1 week to prevent flash of events in the UI when toggling to next week.
      return {
        startsAt: focusedDateTime.startOf('day').minus({days: daysBefore}).valueOf(),
        endsAt: focusedDateTime.endOf('day').plus({days: daysAfter, weeks: 1}).valueOf(),
      };
    }

    case CalendarsViewTypesEnum.MONTH: {
      // Figure out the size of the left & right padding around the month.
      const weekdays = getSortedWeekdays(weekStartDay);
      const startOfFocusedMonth = focusedDateTime.startOf('month');
      const previousPad = weekdays.indexOf(startOfFocusedMonth.weekday);
      const rows = Math.ceil((focusedDateTime.daysInMonth + previousPad) / 7);
      const totalBoxes = rows * 7;
      const nextPad = totalBoxes - (previousPad + focusedDateTime.daysInMonth);

      // Add the padding on both sides of the range.
      return {
        startsAt: focusedDateTime.startOf('month').minus({days: previousPad}).valueOf(),
        endsAt: focusedDateTime.endOf('month').plus({days: nextPad}).valueOf(),
      };
    }

    case CalendarsViewTypesEnum.REMINDERS:
      return {
        startsAt: focusedDateTime.startOf('day').valueOf(),
        endsAt: focusedDateTime.plus({weeks: 2}).endOf('day').valueOf(),
      };

    default:
      throw new Error('This should not happen.');
  }
}

export function findParametersCalendarVersionIds(parameters: CalendarsParameters) {
  return union(parameters.selectedCalendarVersionIds, parameters.viewingNowCalendarVersionIds);
}
